/**
 * AideListingView - Regroupe les ACHAT non-attribués par Match > Catégorie > Block > Rang > Places contiguës
 */
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, MapPin, Copy, Check, Download, CheckSquare, Square, Radio, Loader2, ScanSearch } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { ticketService } from '../../services/api';
// 🔒 Extension isolée — supprime cet import + le bouton + le composant pour retirer la feature
import ReachProCompareModal from './ReachProCompareModal';

// Helper: build text for a match/category/range
const buildRangeText = (matchLabel, cat, block, rang, range) => {
  return `${matchLabel} | ${cat} | Block ${block} | Rang ${rang} | Places ${range.start}${range.end !== range.start ? `-${range.end}` : ''} (${range.count})`;
};

// Extrait UNIQUEMENT le nom des équipes depuis un libellé d'événement WC complet.
// Exemples :
//   "M1 Mexico vs South Africa - World Cup - Group A (Match 1)" → "Mexico vs South Africa"
//   "(GROUPE A) - Mexique - Afrique du Sud"                     → "Mexique - Afrique du Sud"
const extractTeamsOnly = (eventName) => {
  if (!eventName) return '';
  let s = String(eventName).trim();
  // Retire "M<num>" en début
  s = s.replace(/^\s*M\d+\b[\s\-:.]*/i, '');
  // Retire "(GROUPE X)" ou "(Group X)" ou "(Match N)"
  s = s.replace(/\(\s*(groupe|group)\s+[A-Z]\s*\)\s*[-–]?\s*/i, '');
  s = s.replace(/\(\s*match\s+\d+\s*\)\s*$/i, '').trim();
  // Retire suffixes "- World Cup - Group A" / "- WC - GROUP A" / ". WC - GROUP A" etc.
  s = s.replace(/\s*[-–·.]\s*(World\s*Cup|WC)\b.*$/i, '').trim();
  // Retire séparateurs/parenthèses orphelines en début/fin
  s = s.replace(/^[\s\-–·]+|[\s\-–·]+$/g, '').trim();
  return s;
};

const buildCategoryText = (matchLabel, cat, placements, withoutCount) => {
  const lines = [`${matchLabel} — ${cat.category}`];
  for (const p of placements) {
    for (const r of p.ranges) {
      lines.push(`  B:${p.block} R:${p.rang} P:${r.start}${r.end !== r.start ? `-${r.end}` : ''} (${r.count} place${r.count > 1 ? 's' : ''})`);
    }
    if (p.noSeatCount > 0) lines.push(`  B:${p.block} R:${p.rang} — ${p.noSeatCount} sans siège`);
  }
  if (withoutCount > 0) lines.push(`  ${withoutCount} billet(s) sans emplacement`);
  return lines.join('\n');
};

const buildMatchText = (match) => {
  const lines = [`M${match.matchNum} ${match.evenement} — ${match.date} — ${match.totalCount} billets`];
  for (const cat of match.categories) {
    lines.push(`\n  ${cat.category} (${cat.totalCount})`);
    for (const p of cat.placements) {
      for (const r of p.ranges) {
        lines.push(`    B:${p.block} R:${p.rang} P:${r.start}${r.end !== r.start ? `-${r.end}` : ''} (${r.count})`);
      }
      if (p.noSeatCount > 0) lines.push(`    B:${p.block} R:${p.rang} — ${p.noSeatCount} sans siège`);
    }
    if (cat.withoutPlacementCount > 0) lines.push(`    ${cat.withoutPlacementCount} sans emplacement`);
  }
  return lines.join('\n');
};

const AideListingView = ({ tickets = [], project = '', loading = false, onTicketsUpdated }) => {
  const [collapsed, setCollapsed] = useState({});
  // 🔒 État isolé pour la feature ReachPro (extension décrochable)
  const [reachProOpen, setReachProOpen] = useState(false);
  // État local optimiste pour les tickets diffusés (id → bool)
  const [diffusedOverrides, setDiffusedOverrides] = useState({});

  // ⏳ Déférer l'agrégation lourde pour permettre au spinner de s'afficher avant que
  // le useMemo (10k+ tickets, ~150 lignes) ne fige le thread React.
  const [processedTickets, setProcessedTickets] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  React.useEffect(() => {
    if (loading) {
      setIsProcessing(true);
      return;
    }
    // Marque l'aggregation comme en cours
    setIsProcessing(true);
    // Délai court pour permettre à React de peindre le spinner AVANT
    // que le calcul lourd `grouped` ne bloque le main thread.
    const id = setTimeout(() => {
      setProcessedTickets(tickets);
      setIsProcessing(false);
    }, 30);
    return () => clearTimeout(id);
  }, [tickets, loading]);
  
  const isTicketDiffused = (t) => {
    if (t.id in diffusedOverrides) return diffusedOverrides[t.id];
    return t.diffused === true;
  };
  
  const handleToggleDiffuse = async (ticketsToToggle) => {
    if (!ticketsToToggle || ticketsToToggle.length === 0) return;
    // Cible : si AU MOINS UN n'est pas diffusé → on diffuse tout. Sinon on retire tout.
    const anyOff = ticketsToToggle.some(t => !isTicketDiffused(t));
    const newDiffused = anyOff;
    const ids = ticketsToToggle.map(t => t.id);
    // Update optimiste local UNIQUEMENT — on NE déclenche PAS onTicketsUpdated
    // sinon le re-fetch des tickets remet les `updated_at` à jour, ce qui
    // peut perturber checkListed() qui s'en sert pour invalider les états.
    setDiffusedOverrides(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = newDiffused; });
      return next;
    });
    try {
      await ticketService.bulkDiffuse(ids, newDiffused);
      toast.success(newDiffused
        ? `📡 ${ids.length} billet${ids.length > 1 ? 's' : ''} diffusé${ids.length > 1 ? 's' : ''}`
        : `${ids.length} billet${ids.length > 1 ? 's' : ''} retiré${ids.length > 1 ? 's' : ''} de la diffusion`);
    } catch (e) {
      // Rollback
      setDiffusedOverrides(prev => {
        const next = { ...prev };
        ids.forEach(id => { next[id] = !newDiffused; });
        return next;
      });
      toast.error('Erreur lors de la diffusion');
    }
  };

  // Persistance "listé / pause manuelle" par projet (localStorage)
  const listedStorageKey = `aideListing_listed_${project}`;
  const [listedMap, setListedMap] = useState(() => {
    try {
      const raw = localStorage.getItem(listedStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const m = {}; const now = new Date().toISOString();
        parsed.forEach(k => { m[k] = { ts: now, state: 'listed' }; });
        return m;
      }
      const m = {};
      Object.entries(parsed || {}).forEach(([k, v]) => {
        if (typeof v === 'string') m[k] = { ts: v, state: 'listed' };
        else if (v && typeof v === 'object') m[k] = { ts: v.ts || new Date().toISOString(), state: v.state || 'listed' };
      });
      return m;
    } catch { return {}; }
  });

  // Persistance "déjà retiré du listing" pour les ventes hors-reach
  // Quand l'utilisateur a cliqué sur le rouge EN PAUSE, on ne réaffiche plus le ticket.
  const delistedStorageKey = `aideListing_delisted_${project}`;
  const [delistedIds, setDelistedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(delistedStorageKey) || '[]')); } catch { return new Set(); }
  });
  const markDelisted = (ticketId) => {
    setDelistedIds(prev => {
      const next = new Set(prev);
      next.add(ticketId);
      try { localStorage.setItem(delistedStorageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  // Retourne l'état: 'listed' | 'paused' | null
  // ⚠️ Logique anti-faux-positif : on n'invalide QUE si la composition de la
  // plage a changé (nouveau ticket ajouté). Modifier le prix/statut d'un ticket
  // existant ou rafraîchir le state ne doit PAS retirer le "Déjà Listé".
  const checkListed = (listedKey, ticketsInRange) => {
    const entry = listedMap[listedKey];
    if (!entry) return null;
    // Si on a la liste des IDs au moment du clic, on compare
    const currentIds = (ticketsInRange || []).map(t => t.id);
    const savedIds = Array.isArray(entry.ids) ? entry.ids : null;
    if (savedIds) {
      const savedSet = new Set(savedIds);
      const hasNewTicket = currentIds.some(id => !savedSet.has(id));
      if (hasNewTicket) return entry.state === 'paused' ? 'paused' : null;
      return entry.state || 'listed';
    }
    // Fallback (ancienne entrée sans ids) : on garde le state tel quel sans timestamp check
    // pour ne pas perdre les états déjà sauvegardés lors d'un refresh
    return entry.state || 'listed';
  };

  // Cycle manuel: null → listed → paused → null
  const toggleListed = (rangeKey, ticketsInRange = []) => {
    setListedMap(prev => {
      const next = { ...prev };
      const cur = next[rangeKey]?.state;
      const ids = (ticketsInRange || []).map(t => t.id).filter(Boolean);
      if (!cur) next[rangeKey] = { ts: new Date().toISOString(), state: 'listed', ids };
      else if (cur === 'listed') next[rangeKey] = { ts: new Date().toISOString(), state: 'paused', ids };
      else delete next[rangeKey];
      try { localStorage.setItem(listedStorageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Persistance des prix saisis par l'utilisateur (par projet)
  const priceStorageKey = `aideListing_prices_${project}`;
  const [priceMap, setPriceMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(priceStorageKey) || '{}'); } catch { return {}; }
  });
  const setPrice = (key, value) => {
    setPriceMap(prev => {
      const next = { ...prev };
      if (value === '' || value === null || value === undefined) delete next[key];
      else next[key] = value;
      try { localStorage.setItem(priceStorageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Persistance des cases "pointage" cochées par l'utilisateur pendant son travail
  // (purement visuel, indépendant des états listed/paused)
  const pointedStorageKey = `aideListing_pointed_${project}`;
  const [pointedSet, setPointedSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(pointedStorageKey) || '[]')); } catch { return new Set(); }
  });
  const togglePointed = (key) => {
    setPointedSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(pointedStorageKey, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Group tickets: Match > Category > Block+Rang > Contiguous seats
  // Utilise `processedTickets` (déféré) pour permettre au spinner d'apparaître
  // avant que le calcul lourd ne bloque le rendu.
  const grouped = useMemo(() => {
    const sourceTickets = processedTickets || [];
    const allAchat = sourceTickets.filter(t => t.type === 'ACHAT');

    // Build a map ref_vente → vente source for hors-reach detection
    const venteSourceByRef = {};
    for (const t of sourceTickets) {
      if (t.type === 'VENTE' && t.ref_vente) {
        venteSourceByRef[t.ref_vente.trim()] = (t.source || '').toUpperCase().replace(/\s+/g, '');
      }
    }

    // Decide which ACHAT tickets to display:
    // - Non-attribués → toujours affichés
    // - Attribués → affichés EN PAUSE sauf si la vente liée est REACH (auto-listée)
    //   ou si l'utilisateur a manuellement délisté
    const REACH_SOURCES = new Set(['REACH01', 'REACH', 'REACH 01']);
    const displayed = allAchat.filter(t => {
      if (!t.attribue) return true;
      if (delistedIds.has(t.id)) return false;
      const vSource = venteSourceByRef[(t.ref_vente || '').trim()] || '';
      // Si on a la confirmation que la vente est REACH → caché (déjà délisté auto)
      if (vSource && REACH_SOURCES.has(vSource)) return false;
      // Sinon (hors-Reach OU vente inconnue) → affiché en EN PAUSE
      return true;
    });

    const getMatchNum = (ev) => {
      const m = (ev || '').match(/M(\d+)/i);
      return m ? parseInt(m[1], 10) : 9999;
    };

    // Group by match — par numéro uniquement pour fusionner les libellés en double
    const byMatch = {};
    for (const t of displayed) {
      const matchNum = getMatchNum(t.evenement);
      const matchKey = `${matchNum}`;
      if (!byMatch[matchKey]) {
        byMatch[matchKey] = {
          matchNum,
          evenement: t.evenement || 'Sans match',
          date: t.date_evenement || '',
          lieu: t.lieu || '',
          tickets: [],
          _labels: [],
        };
      }
      // Conserve le libellé le plus long (souvent le plus complet)
      const lbl = (t.evenement || '').trim();
      if (lbl && lbl.length > (byMatch[matchKey].evenement || '').length) {
        byMatch[matchKey].evenement = lbl;
      }
      if (lbl && !byMatch[matchKey]._labels.includes(lbl)) byMatch[matchKey]._labels.push(lbl);
      // Date : on prend la première non-vide trouvée
      if (!byMatch[matchKey].date && t.date_evenement) byMatch[matchKey].date = t.date_evenement;
      byMatch[matchKey].tickets.push(t);
    }

    // Ajout des matchs "vides" : tous les matchs existants en base (ACHAT + VENTE)
    // même si aucun ticket à lister, pour que l'utilisateur ait la vue complète
    for (const t of sourceTickets) {
      const matchNum = getMatchNum(t.evenement);
      if (matchNum === 9999) continue;
      const matchKey = `${matchNum}`;
      if (!byMatch[matchKey]) {
        byMatch[matchKey] = {
          matchNum,
          evenement: t.evenement || 'Sans match',
          date: t.date_evenement || '',
          lieu: t.lieu || '',
          tickets: [],
          _labels: [],
          _empty: true,
        };
      } else {
        // Si le libellé courant est plus court, on garde la version plus complète
        const lbl = (t.evenement || '').trim();
        if (lbl && lbl.length > (byMatch[matchKey].evenement || '').length) {
          byMatch[matchKey].evenement = lbl;
        }
      }
    }

    // Sort matches by match number
    const sortedMatches = Object.values(byMatch).sort((a, b) => a.matchNum - b.matchNum);

    // For each match, group by category > block+rang > contiguous seats
    return sortedMatches.map(match => {
      const byCat = {};
      for (const t of match.tickets) {
        const cat = t.categorie || 'SANS CATÉGORIE';
        if (!byCat[cat]) byCat[cat] = [];
        byCat[cat].push(t);
      }

      const categories = Object.entries(byCat).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catTickets]) => {
        // Separate tickets with and without placement
        // "Sans emplacement" ne doit afficher QUE les tickets non-attribués
        // (les attribués hors-reach restent groupés par block/rang ailleurs)
        const withPlacement = catTickets.filter(t => t.block || t.rang);
        const withoutPlacement = catTickets.filter(t => !t.block && !t.rang && !t.attribue);

        // Group by block+rang
        const byBlockRang = {};
        for (const t of withPlacement) {
          const key = `${t.block || '-'}|${t.rang || '-'}`;
          if (!byBlockRang[key]) {
            byBlockRang[key] = { block: t.block || '', rang: t.rang || '', tickets: [] };
          }
          byBlockRang[key].tickets.push(t);
        }

        // For each block+rang group, find contiguous seat ranges
        const placements = Object.values(byBlockRang).map(group => {
          // Build a quick index id -> updated_at for tickets in this block/rang
          const ticketIndex = {};
          for (const t of group.tickets) ticketIndex[t.id] = t.updated_at;

          const seats = group.tickets
            .map(t => ({
              place: parseInt(t.place, 10), id: t.id,
              email: t.email_compte || '', source: t.source || '',
              updated_at: t.updated_at, attribue: !!t.attribue,
              ref_vente: t.ref_vente || '',
              diffused: t.diffused === true,
            }))
            .filter(s => !isNaN(s.place))
            .sort((a, b) => a.place - b.place);

          const noSeatTickets = group.tickets.filter(t => !t.place && !parseInt(t.place, 10) && !t.attribue);
          const noSeatSources = new Set();
          const noSeatEmails = new Set();
          const noSeatTicketRefs = [];
          for (const t of noSeatTickets) {
            if (t.source) noSeatSources.add(t.source);
            if (t.email_compte) noSeatEmails.add(t.email_compte);
            noSeatTicketRefs.push({ id: t.id, updated_at: t.updated_at, diffused: t.diffused });
          }
          const noSeat = noSeatTickets;

          // Find contiguous ranges — split when attribue status changes between seats
          const ranges = [];
          let current = null;
          for (const s of seats) {
            const continues = current && s.place === current.end + 1 && s.attribue === current.attribue;
            if (continues) {
              current.end = s.place;
              current.count++;
              current.emails.add(s.email);
              current.sources.add(s.source);
              current.tickets.push({id: s.id, updated_at: s.updated_at, diffused: s.diffused});
              if (s.ref_vente) current.refVentes.add(s.ref_vente);
            } else {
              if (current) ranges.push(current);
              current = {
                start: s.place, end: s.place, count: 1,
                emails: new Set([s.email]), sources: new Set([s.source]),
                tickets: [{id: s.id, updated_at: s.updated_at, diffused: s.diffused}],
                attribue: s.attribue,
                refVentes: new Set(s.ref_vente ? [s.ref_vente] : []),
              };
            }
          }
          if (current) ranges.push(current);

          return {
            block: group.block,
            rang: group.rang,
            ranges,
            noSeatCount: noSeat.length,
            noSeatSources,
            noSeatEmails,
            noSeatTickets: noSeatTicketRefs,
            totalCount: group.tickets.length,
          };
        }).sort((a, b) => {
          const ba = parseInt(a.block, 10) || 0;
          const bb = parseInt(b.block, 10) || 0;
          if (ba !== bb) return ba - bb;
          const ra = parseInt(a.rang, 10) || 0;
          const rb = parseInt(b.rang, 10) || 0;
          return ra - rb;
        });

        // Group "sans emplacement" par source pour identifier la provenance
        const wpBySrc = {};
        for (const t of withoutPlacement) {
          const src = t.source || 'INCONNUE';
          if (!wpBySrc[src]) wpBySrc[src] = { source: src, count: 0, emails: new Set(), tickets: [] };
          wpBySrc[src].count++;
          if (t.email_compte) wpBySrc[src].emails.add(t.email_compte);
          wpBySrc[src].tickets.push({ id: t.id, updated_at: t.updated_at, diffused: t.diffused });
        }
        const withoutPlacementGroups = Object.values(wpBySrc).sort((a, b) => a.source.localeCompare(b.source));

        return {
          category: cat,
          placements,
          withoutPlacementCount: withoutPlacement.length,
          withoutPlacementGroups,
          totalCount: catTickets.length,
        };
      });

      return {
        ...match,
        categories,
        totalCount: match.tickets.length,
      };
    });
  }, [processedTickets, delistedIds]);

  // Compteurs adaptés au filtre actif
  const totalTickets = grouped.reduce((s, m) => s + m.totalCount, 0);

  // Filtre par état : 'all' | 'todo' | 'listed' | 'paused'
  const [filterState, setFilterState] = useState('all');

  // Calcule la visibilité de chaque ligne en fonction du filtre actif.
  // Utilise EXACTEMENT la même logique que les boutons (checkListed) pour
  // garantir la cohérence visuelle entre le badge et l'affichage.
  const visibility = useMemo(() => {
    if (filterState === 'all') return null;
    const rowOK = new Set();      // clés listedKey visibles
    const catOK = new Set();      // `${matchNum}|${catName}`
    const matchOK = new Set();    // matchNum

    // état affiché pour une ligne donnée — identique au calcul du bouton
    const stateOfRow = (key, attribue, ticketsArr) => {
      if (attribue) return 'paused';          // vente hors-reach
      const s = checkListed(key, ticketsArr); // 'listed' | 'paused' | null
      return s || 'todo';
    };

    for (const match of grouped) {
      let matchHas = false;
      for (const cat of match.categories) {
        let catHas = false;
        for (const p of cat.placements) {
          for (const range of p.ranges) {
            const k = `M${match.matchNum}|${cat.category}|${p.block}|${p.rang}|${range.start}-${range.end}`;
            if (stateOfRow(k, range.attribue, range.tickets) === filterState) {
              rowOK.add(k); catHas = true;
            }
          }
          if (p.noSeatCount > 0) {
            const k = `M${match.matchNum}|${cat.category}|${p.block}|${p.rang}|NOSEAT`;
            if (stateOfRow(k, false, p.noSeatTickets) === filterState) {
              rowOK.add(k); catHas = true;
            }
          }
        }
        for (const wg of (cat.withoutPlacementGroups || [])) {
          const k = `M${match.matchNum}|${cat.category}|NOPLACE|${wg.source}`;
          if (stateOfRow(k, false, wg.tickets) === filterState) {
            rowOK.add(k); catHas = true;
          }
        }
        if (catHas) { catOK.add(`${match.matchNum}|${cat.category}`); matchHas = true; }
      }
      if (matchHas) matchOK.add(match.matchNum);
    }
    return { rowOK, catOK, matchOK };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped, listedMap, filterState]);

  const isRowVisible = (key) => !visibility || visibility.rowOK.has(key);

  // Composant input prix réutilisable — validation par Entrée, modifiable à tout moment
  const PriceInput = React.memo(({ listedKey }) => {
    const stored = priceMap[listedKey] ?? '';
    const [draft, setDraft] = useState(String(stored));
    React.useEffect(() => { setDraft(String(priceMap[listedKey] ?? '')); }, [listedKey]);

    const commit = () => {
      const cleaned = draft.replace(',', '.').trim();
      if (cleaned === '') { setPrice(listedKey, ''); return; }
      const n = parseFloat(cleaned);
      if (!isNaN(n) && n >= 0) {
        setPrice(listedKey, n);
        setDraft(String(n));
      } else {
        setDraft(String(stored));
      }
    };

    return (
      <div className="flex items-center gap-1 bg-zinc-900/80 border border-zinc-700 rounded px-2 py-1 hover:border-[#C4A052]/60 focus-within:border-[#C4A052] transition-colors">
        <input
          type="text"
          inputMode="decimal"
          value={draft}
          placeholder="—"
          onChange={(e) => {
            // Autorise uniquement chiffres + 1 point/virgule
            const v = e.target.value.replace(/[^0-9.,]/g, '').replace(/([.,]).*?[.,]/g, '$1');
            setDraft(v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
            else if (e.key === 'Escape') { setDraft(String(stored)); e.currentTarget.blur(); }
          }}
          onBlur={commit}
          onClick={(e) => e.stopPropagation()}
          className="bg-transparent text-sm text-white w-20 text-right focus:outline-none placeholder:text-zinc-600 font-mono"
          data-testid={`price-input-${listedKey}`}
          title="Saisis le prix (Entrée pour valider, Échap pour annuler)"
        />
        <span className="text-xs text-zinc-400">€</span>
      </div>
    );
  });
  const isCatVisible = (matchNum, catName) => !visibility || visibility.catOK.has(`${matchNum}|${catName}`);
  const isMatchVisible = (matchNum) => !visibility || visibility.matchOK.has(matchNum);
  const visibleMatchCount = visibility ? visibility.matchOK.size : grouped.length;

  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast.success('Copié !');
  };

  const exportCSV = () => {
    const rows = [['Match', 'Date', 'Catégorie', 'Block', 'Rang', 'Places', 'Nb', 'Côte à côte', 'Source', 'Emails']];
    for (const match of grouped) {
      const ml = `M${match.matchNum} ${match.evenement}`;
      for (const cat of match.categories) {
        for (const p of cat.placements) {
          for (const r of p.ranges) {
            const srcs = r.sources ? [...r.sources].filter(Boolean).join(' / ') : '';
            rows.push([ml, match.date, cat.category, p.block, p.rang, r.start === r.end ? `${r.start}` : `${r.start}-${r.end}`, r.count, r.count > 1 ? 'Oui' : '', srcs, [...r.emails].filter(Boolean).join('; ')]);
          }
          if (p.noSeatCount > 0) rows.push([ml, match.date, cat.category, p.block, p.rang, '', p.noSeatCount, '', '', '']);
        }
        if (cat.withoutPlacementCount > 0) rows.push([ml, match.date, cat.category, '', '', 'Sans emplacement', cat.withoutPlacementCount, '', '', '']);
      }
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aide_listing_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté !');
  };

  // ⏳ Loader pendant que les tickets se chargent (fetch backend) OU que l'agrégation
  //    locale est en cours (useMemo lourd avec 10k+ tickets).
  if (loading || isProcessing || processedTickets === null) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-zinc-400" data-testid="aide-listing-loading">
        <div className="relative">
          <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full animate-pulse" />
          <Loader2 className="h-12 w-12 text-amber-400 animate-spin relative" />
        </div>
        <p className="mt-4 text-base font-semibold text-zinc-200">
          {loading ? 'Chargement des billets...' : 'Agrégation en cours...'}
        </p>
        <p className="text-xs text-zinc-500 mt-1">
          {loading
            ? 'Récupération depuis le serveur'
            : `Traitement de ${tickets.length} billets — veuillez patienter`}
        </p>
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500">
        <MapPin className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-lg">Aucun ticket ACHAT non-attribué</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aide-listing-view">
      {/* Header */}
      <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-2 flex-wrap gap-2">
        <span className="text-sm text-zinc-300">
          <strong className="text-[#C4A052]">{totalTickets}</strong> billets ACHAT à traiter
          {visibility ? (
            <> · <strong className="text-emerald-400">{visibleMatchCount}</strong> match{visibleMatchCount > 1 ? 's' : ''} affiché{visibleMatchCount > 1 ? 's' : ''}</>
          ) : (() => {
            const totalMatches = grouped.length;
            const emptyMatches = grouped.filter(m => m.totalCount === 0).length;
            const activeMatches = totalMatches - emptyMatches;
            return (
              <>
                {' · '}
                <strong>{totalMatches}</strong> match{totalMatches > 1 ? 's' : ''} au total
                {emptyMatches > 0 && (
                  <span className="text-emerald-400/80"> ({emptyMatches} ✓ tout listé{emptyMatches > 1 ? 's' : ''})</span>
                )}
                {activeMatches > 0 && activeMatches !== totalMatches && (
                  <span className="text-[#C4A052]/80"> · {activeMatches} à traiter</span>
                )}
              </>
            );
          })()}
        </span>
        <div className="flex items-center gap-1.5">
          {[
            { id: 'all', label: 'TOUS', color: 'bg-zinc-700 hover:bg-zinc-600 text-zinc-200 border-zinc-600' },
            { id: 'todo', label: 'À LISTER', color: 'bg-zinc-700/60 hover:bg-zinc-600 text-zinc-200 border-zinc-500' },
            { id: 'listed', label: '✓ DÉJÀ LISTÉ', color: 'bg-blue-600 hover:bg-blue-500 text-white border-blue-400' },
            { id: 'paused', label: '⏸ EN PAUSE', color: 'bg-red-600 hover:bg-red-500 text-white border-red-400' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilterState(f.id)}
              className={cn(
                "px-3 py-1 text-xs font-bold rounded border transition-all",
                f.color,
                filterState === f.id ? "ring-2 ring-[#C4A052] ring-offset-1 ring-offset-zinc-900" : "opacity-70 hover:opacity-100"
              )}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={exportCSV} className="bg-[#C4A052] hover:bg-[#D4B86A] text-black" data-testid="export-listing-csv">
          <Download className="h-4 w-4 mr-1" /> Exporter CSV
        </Button>
        {/* 🔒 Extension ReachPro — retirer ce bouton + l'import + le fichier ReachProCompareModal.jsx pour supprimer la feature */}
        <Button
          size="sm"
          onClick={() => setReachProOpen(true)}
          variant="outline"
          className="border-[#C4A052]/40 text-[#C4A052] hover:bg-[#C4A052]/10 hover:text-[#D4B86A]"
          data-testid="btn-open-reachpro-compare"
          title="Comparer l'inventaire ReachPro avec la base YAS"
        >
          <ScanSearch className="h-4 w-4 mr-1" /> Comparer ReachPro
        </Button>
      </div>

      {/* 🔒 Extension ReachPro — modal isolé, ne touche aucun état parent */}
      <ReachProCompareModal
        open={reachProOpen}
        onOpenChange={setReachProOpen}
        tickets={tickets}
      />

      {/* Matches */}
      {grouped.map((match) => {
        const isEmpty = match.totalCount === 0;
        // Quand un filtre autre que TOUS est actif, les matchs vides sont masqués
        if (visibility && isEmpty) return null;
        if (!isMatchVisible(match.matchNum) && !isEmpty) return null;
        const matchKey = `match-${match.matchNum}`;
        // Quand un filtre est actif, on auto-déplie les matchs/catégories
        // qui contiennent une ligne visible (sinon UX confus : badge + carte vide)
        const isCollapsed = visibility ? false : (isEmpty ? true : collapsed[matchKey]);
        const matchLabel = match.evenement.length > 60 ? match.evenement.slice(0, 57) + '...' : match.evenement;
        const rugbySplit = (project || '').toUpperCase().includes('RUGBY')
          ? (match.evenement || '').match(/^M\d+\s*-\(\s*([^)]*)\)\s*-\s*(.+)$/i)
          : null;

        return (
          <div
            key={matchKey}
            className={cn(
              "border rounded-lg overflow-hidden",
              isEmpty ? "border-emerald-700/30 bg-emerald-950/10" : "border-zinc-700"
            )}
            data-testid={`listing-match-${match.matchNum}`}
          >
            {/* Match header */}
            <button
              onClick={() => toggleCollapse(matchKey)}
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 transition-colors",
                isEmpty ? "bg-emerald-950/20 hover:bg-emerald-950/30" : "bg-zinc-800 hover:bg-zinc-750"
              )}
              disabled={isEmpty}
            >
              <div className="flex items-center gap-3">
                {isEmpty ? (
                  <Check className="h-4 w-4 text-emerald-500" />
                ) : (
                  isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-[#C4A052]" />
                )}
                {!rugbySplit && (
                  <span className={cn(
                    "text-xs font-bold px-2 py-0.5 rounded",
                    isEmpty ? "bg-emerald-700/40 text-emerald-200" : "bg-[#C4A052] text-black"
                  )}>M{match.matchNum}</span>
                )}
                <span
                  className={cn(
                    "cursor-copy hover:underline decoration-dotted underline-offset-4",
                    rugbySplit ? "text-base font-extrabold" : "text-sm font-medium",
                    isEmpty ? "text-emerald-200/80" : "text-white"
                  )}
                  title="Cliquer pour copier le nom des équipes uniquement"
                  data-testid={`copy-teams-${match.matchNum}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const teams = extractTeamsOnly(match.evenement);
                    if (teams) doCopy(`teams-${match.matchNum}`, teams);
                  }}
                >
                  {rugbySplit ? rugbySplit[2].trim() : matchLabel}
                  {copiedKey === `teams-${match.matchNum}` && (
                    <Check className="inline-block h-3 w-3 ml-1 text-emerald-400" />
                  )}
                </span>
                {rugbySplit && (
                  <span className="text-xs text-[#C4A052] font-semibold">( {rugbySplit[1].trim()} )</span>
                )}
                {match.date && <span className="text-xs text-zinc-500">{match.date}</span>}
              </div>
              {isEmpty ? (
                <span className="text-xs text-emerald-400 font-bold">✓ Tout listé</span>
              ) : (
                <span className="text-sm text-[#C4A052] font-bold">{match.totalCount} billet{match.totalCount > 1 ? 's' : ''}</span>
              )}
              {!isEmpty && (
                <Button size="sm" variant="ghost" className="h-6 px-1 text-zinc-500 hover:text-white ml-2" onClick={(e) => { e.stopPropagation(); doCopy(`match-${match.matchNum}`, buildMatchText(match)); }}>
                  {copiedKey === `match-${match.matchNum}` ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              )}
            </button>

            {/* Categories */}
            {!isCollapsed && (
              <div className="divide-y divide-zinc-800">
                {match.categories.map((cat) => {
                  if (!isCatVisible(match.matchNum, cat.category)) return null;
                  const catKey = `${matchKey}-${cat.category}`;
                  const isCatCollapsed = visibility ? false : collapsed[catKey];

                  return (
                    <div key={catKey}>
                      {/* Category header */}
                      <button
                        onClick={() => toggleCollapse(catKey)}
                        className="w-full flex items-center justify-between px-4 py-2 pl-8 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {isCatCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-zinc-500" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />}
                          <span className="text-sm text-zinc-200 font-medium">{cat.category}</span>
                        </div>
                        <span className="text-xs text-zinc-400">{cat.totalCount} billet{cat.totalCount > 1 ? 's' : ''}</span>
                        <Button size="sm" variant="ghost" className="h-5 px-1 text-zinc-500 hover:text-white" onClick={(e) => { e.stopPropagation(); doCopy(catKey, buildCategoryText(matchLabel, cat, cat.placements, cat.withoutPlacementCount)); }}>
                          {copiedKey === catKey ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                        </Button>
                      </button>

                      {/* Placements */}
                      {!isCatCollapsed && (
                        <div className="px-4 py-2 pl-14 space-y-1.5 bg-zinc-900/30">
                          {cat.placements.map((p, pi) => (
                            <div key={pi}>
                              {p.ranges.map((range, ri) => {
                                const rangeKey = `${p.block}-${p.rang}-${range.start}`;
                                const listedKey = `M${match.matchNum}|${cat.category}|${p.block}|${p.rang}|${range.start}-${range.end}`;
                                if (!isRowVisible(listedKey)) return null;
                                const isHorsReachSale = range.attribue;
                                const manualState = checkListed(listedKey, range.tickets);
                                const paused = isHorsReachSale || manualState === 'paused';
                                const listed = !paused && manualState === 'listed';
                                const isContiguous = range.count > 1;
                                const refVenteLabel = isHorsReachSale && range.refVentes.size > 0
                                  ? [...range.refVentes].join(', ') : '';
                                return (
                                  <div
                                    key={ri}
                                    className={cn(
                                      "flex items-center gap-3 px-3 py-1.5 rounded text-sm transition-all",
                                      pointedSet.has(listedKey) && "opacity-60",
                                      paused ? "bg-red-950/80 border-2 border-red-500/70 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.3)]"
                                        : listed ? "bg-blue-950/80 border-2 border-blue-500/70 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.3)]"
                                        : isContiguous ? "bg-emerald-500/10 border border-emerald-500/20"
                                        : "bg-zinc-800/50"
                                    )}
                                  >
                                    <button
                                      onClick={() => togglePointed(listedKey)}
                                      className={cn("flex-shrink-0 transition-colors hover:scale-110",
                                        pointedSet.has(listedKey) ? "text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                                      )}
                                      title={pointedSet.has(listedKey) ? "Décocher (pointage)" : "Cocher pour pointer cette ligne"}
                                      data-testid={`pointed-${listedKey}`}
                                    >
                                      {pointedSet.has(listedKey) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                    </button>
                                    <span className={cn("font-semibold min-w-[70px]", paused ? "text-red-200" : listed ? "text-blue-200" : "text-[#C4A052]")}>B:{p.block}</span>
                                    <span className={cn("font-semibold min-w-[50px]", paused ? "text-red-200" : listed ? "text-blue-200" : "text-[#C4A052]")}>R:{p.rang}</span>
                                    <span className={cn("font-bold min-w-[100px]",
                                      paused ? "text-red-200" : listed ? "text-blue-200" : (isContiguous ? "text-emerald-400" : "text-zinc-300")
                                    )}>
                                      {range.start === range.end
                                        ? `P:${range.start}`
                                        : `P:${range.start}-${range.end}`}
                                    </span>
                                    <span className={cn("text-xs px-1.5 py-0.5 rounded",
                                      paused ? "bg-red-500/20 text-red-100" : listed ? "bg-blue-500/20 text-blue-100" : (isContiguous ? "bg-emerald-500/20 text-emerald-300" : "text-zinc-500")
                                    )}>
                                      {isContiguous && range.count > 1
                                        ? `Lot de ${range.count}`
                                        : `${range.count} place${range.count > 1 ? 's' : ''}`}
                                    </span>
                                    {range.sources && [...range.sources].filter(Boolean).length > 0 && (
                                      <span className={cn("text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide",
                                        paused
                                          ? "bg-red-700/40 text-red-100"
                                          : listed
                                          ? "bg-blue-700/40 text-blue-100"
                                          : "bg-[#C4A052]/15 text-[#C4A052] border border-[#C4A052]/30"
                                      )}>
                                        {[...range.sources].filter(Boolean).join(' / ')}
                                      </span>
                                    )}
                                    {range.emails.size > 0 && [...range.emails].filter(Boolean).length > 0 && (
                                      <span className={cn("text-xs truncate max-w-[200px]", paused ? "text-red-200/80" : listed ? "text-blue-200/80" : "text-zinc-500")}>
                                        {[...range.emails].filter(Boolean).join(', ')}
                                      </span>
                                    )}
                                    {refVenteLabel && (
                                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-700/40 text-red-100 border border-red-500/40" title="Référence de vente hors-Reach">
                                        VENDU {refVenteLabel}
                                      </span>
                                    )}
                                    {isHorsReachSale ? (
                                      <div className="flex items-center gap-2 ml-auto">
                                        <PriceInput listedKey={listedKey} />
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => range.tickets.forEach(t => markDelisted(t.id))}
                                          className="h-7 px-3 text-xs font-bold rounded bg-red-500 text-white hover:bg-red-600 shadow-md ring-1 ring-red-300"
                                          data-testid={`btn-delist-${listedKey}`}
                                          title="J'ai retiré ce billet des plateformes de listing"
                                        >
                                          ⏸ RETIRER DU LISTING
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2 ml-auto">
                                        <PriceInput listedKey={listedKey} />
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleToggleDiffuse(range.tickets)}
                                          className={cn(
                                            "h-7 px-2 text-xs font-bold rounded-full transition-all flex items-center gap-1.5 min-w-[110px] justify-center",
                                            range.tickets.every(t => isTicketDiffused(t))
                                              ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md ring-1 ring-emerald-300"
                                              : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-600"
                                          )}
                                          data-testid={`btn-diffuse-${listedKey}`}
                                          title={range.tickets.every(t => isTicketDiffused(t))
                                            ? "Diffusé sur la page publique — clic pour retirer"
                                            : "Cliquer pour diffuser sur la page publique"}
                                        >
                                          <span className={cn(
                                            "inline-block h-3 w-3 rounded-full transition-transform",
                                            range.tickets.every(t => isTicketDiffused(t))
                                              ? "bg-white translate-x-0"
                                              : "bg-zinc-500"
                                          )} />
                                          <Radio className="h-3 w-3" />
                                          {range.tickets.every(t => isTicketDiffused(t)) ? 'DIFFUSÉ · ON' : 'DIFFUSER · OFF'}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => toggleListed(listedKey, range.tickets)}
                                          className={cn(
                                            "h-7 px-3 text-xs font-bold rounded transition-all",
                                            listed
                                              ? "bg-blue-500 text-white hover:bg-blue-600 shadow-md ring-1 ring-blue-300"
                                              : manualState === 'paused'
                                              ? "bg-red-500 text-white hover:bg-red-600 shadow-md ring-1 ring-red-300"
                                              : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700 border border-zinc-600"
                                          )}
                                          data-testid={`btn-listed-${listedKey}`}
                                          title={manualState === 'paused' ? "Clic : retour à À LISTER" : listed ? "Clic : passer en PAUSE manuelle" : "Clic : marquer DÉJÀ LISTÉ"}
                                        >
                                          {manualState === 'paused' ? '⏸ EN PAUSE' : listed ? '✓ DÉJÀ LISTÉ' : 'LISTER'}
                                        </Button>
                                      </div>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className={cn("h-5 px-1 hover:text-white", paused ? "text-red-200" : listed ? "text-blue-200" : "text-zinc-500")}
                                      onClick={() => doCopy(rangeKey, buildRangeText(matchLabel, cat.category, p.block, p.rang, range))}
                                    >
                                      {copiedKey === rangeKey ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                                    </Button>
                                  </div>
                                );
                              })}
                              {p.noSeatCount > 0 && (() => {
                                const noSeatListedKey = `M${match.matchNum}|${cat.category}|${p.block}|${p.rang}|NOSEAT`;
                                if (!isRowVisible(noSeatListedKey)) return null;
                                const noSeatRangeKey = `${p.block}-${p.rang}-NOSEAT`;
                                const noSeatManualState = checkListed(noSeatListedKey, p.noSeatTickets);
                                const noSeatPaused = noSeatManualState === 'paused';
                                const noSeatListed = !noSeatPaused && noSeatManualState === 'listed';
                                const noSeatSrcList = p.noSeatSources ? [...p.noSeatSources].filter(Boolean) : [];
                                const noSeatEmailList = p.noSeatEmails ? [...p.noSeatEmails].filter(Boolean) : [];
                                return (
                                  <div
                                    className={cn(
                                      "flex items-center gap-3 px-3 py-1.5 rounded text-sm transition-all",
                                      pointedSet.has(noSeatListedKey) && "opacity-60",
                                      noSeatPaused ? "bg-red-950/80 border-2 border-red-500/70 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.3)]"
                                        : noSeatListed ? "bg-blue-950/80 border-2 border-blue-500/70 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.3)]"
                                        : "bg-zinc-800/50"
                                    )}
                                  >
                                    <button
                                      onClick={() => togglePointed(noSeatListedKey)}
                                      className={cn("flex-shrink-0 transition-colors hover:scale-110",
                                        pointedSet.has(noSeatListedKey) ? "text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                                      )}
                                      title={pointedSet.has(noSeatListedKey) ? "Décocher (pointage)" : "Cocher pour pointer cette ligne"}
                                      data-testid={`pointed-${noSeatListedKey}`}
                                    >
                                      {pointedSet.has(noSeatListedKey) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                    </button>
                                    <span className={cn("font-semibold min-w-[70px]", noSeatPaused ? "text-red-200" : noSeatListed ? "text-blue-200" : "text-[#C4A052]")}>B:{p.block}</span>
                                    <span className={cn("font-semibold min-w-[50px]", noSeatPaused ? "text-red-200" : noSeatListed ? "text-blue-200" : "text-[#C4A052]")}>R:{p.rang}</span>
                                    <span className={cn("min-w-[100px]", noSeatPaused ? "text-red-200" : noSeatListed ? "text-blue-200" : "text-zinc-400")}>
                                      {p.noSeatCount} place{p.noSeatCount > 1 ? 's' : ''} sans siège
                                    </span>
                                    {noSeatSrcList.length > 0 && (
                                      <span className={cn("text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide",
                                        noSeatPaused ? "bg-red-700/40 text-red-100"
                                          : noSeatListed ? "bg-blue-700/40 text-blue-100"
                                          : "bg-[#C4A052]/15 text-[#C4A052] border border-[#C4A052]/30"
                                      )}>
                                        {noSeatSrcList.join(' / ')}
                                      </span>
                                    )}
                                    {noSeatEmailList.length > 0 && (
                                      <span className={cn("text-xs truncate max-w-[200px]", noSeatPaused ? "text-red-200/80" : noSeatListed ? "text-blue-200/80" : "text-zinc-500")}>
                                        {noSeatEmailList.join(', ')}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-2 ml-auto">
                                      <PriceInput listedKey={noSeatListedKey} />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleToggleDiffuse(p.noSeatTickets || [])}
                                        className={cn(
                                          "h-7 px-2 text-xs font-bold rounded-full transition-all flex items-center gap-1.5 min-w-[110px] justify-center",
                                          (p.noSeatTickets || []).length > 0 && (p.noSeatTickets || []).every(t => isTicketDiffused(t))
                                            ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md ring-1 ring-emerald-300"
                                            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-600"
                                        )}
                                        data-testid={`btn-diffuse-${noSeatListedKey}`}
                                        title="Diffuser sur la page publique"
                                      >
                                        <span className={cn(
                                          "inline-block h-3 w-3 rounded-full",
                                          (p.noSeatTickets || []).length > 0 && (p.noSeatTickets || []).every(t => isTicketDiffused(t))
                                            ? "bg-white"
                                            : "bg-zinc-500"
                                        )} />
                                        <Radio className="h-3 w-3" />
                                        {(p.noSeatTickets || []).length > 0 && (p.noSeatTickets || []).every(t => isTicketDiffused(t)) ? 'DIFFUSÉ · ON' : 'DIFFUSER · OFF'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => toggleListed(noSeatListedKey, p.noSeatTickets || [])}
                                        className={cn(
                                          "h-7 px-3 text-xs font-bold rounded transition-all",
                                          noSeatListed
                                            ? "bg-blue-500 text-white hover:bg-blue-600 shadow-md ring-1 ring-blue-300"
                                            : noSeatPaused
                                            ? "bg-red-500 text-white hover:bg-red-600 shadow-md ring-1 ring-red-300"
                                            : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700 border border-zinc-600"
                                        )}
                                        data-testid={`btn-listed-${noSeatListedKey}`}
                                        title={noSeatPaused ? "Clic : retour à À LISTER" : noSeatListed ? "Clic : passer en PAUSE manuelle" : "Clic : marquer DÉJÀ LISTÉ"}
                                      >
                                        {noSeatPaused ? '⏸ EN PAUSE' : noSeatListed ? '✓ DÉJÀ LISTÉ' : 'LISTER'}
                                      </Button>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className={cn("h-5 px-1 hover:text-white", noSeatPaused ? "text-red-200" : noSeatListed ? "text-blue-200" : "text-zinc-500")}
                                      onClick={() => doCopy(noSeatRangeKey, `${matchLabel} | ${cat.category} | Block ${p.block} | Rang ${p.rang} | ${p.noSeatCount} sans siège`)}
                                    >
                                      {copiedKey === noSeatRangeKey ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                                    </Button>
                                  </div>
                                );
                              })()}
                            </div>
                          ))}

                          {cat.withoutPlacementCount > 0 && (cat.withoutPlacementGroups || []).map((wg, wi) => {
                            const wpListedKey = `M${match.matchNum}|${cat.category}|NOPLACE|${wg.source}`;
                            if (!isRowVisible(wpListedKey)) return null;
                            const wpRangeKey = `noplace-${cat.category}-${wg.source}-${wi}`;
                            const wpManualState = checkListed(wpListedKey, wg.tickets);
                            const wpPaused = wpManualState === 'paused';
                            const wpListed = !wpPaused && wpManualState === 'listed';
                            const wpEmailList = wg.emails ? [...wg.emails].filter(Boolean) : [];
                            return (
                              <div
                                key={wpRangeKey}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-1.5 rounded text-sm transition-all",
                                  pointedSet.has(wpListedKey) && "opacity-60",
                                  wpPaused ? "bg-red-950/80 border-2 border-red-500/70 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.3)]"
                                    : wpListed ? "bg-blue-950/80 border-2 border-blue-500/70 shadow-[inset_0_0_0_1px_rgba(59,130,246,0.3)]"
                                    : "bg-yellow-500/5 border border-yellow-500/20"
                                )}
                              >
                                <button
                                  onClick={() => togglePointed(wpListedKey)}
                                  className={cn("flex-shrink-0 transition-colors hover:scale-110",
                                    pointedSet.has(wpListedKey) ? "text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                                  )}
                                  title={pointedSet.has(wpListedKey) ? "Décocher (pointage)" : "Cocher pour pointer cette ligne"}
                                  data-testid={`pointed-${wpListedKey}`}
                                >
                                  {pointedSet.has(wpListedKey) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                                </button>
                                <span className={cn("text-xs font-medium min-w-[180px]",
                                  wpPaused ? "text-red-200" : wpListed ? "text-blue-200" : "text-yellow-400"
                                )}>
                                  {wg.count} billet{wg.count > 1 ? 's' : ''} sans emplacement
                                </span>
                                <span className={cn("text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide",
                                  wpPaused ? "bg-red-700/40 text-red-100"
                                    : wpListed ? "bg-blue-700/40 text-blue-100"
                                    : "bg-[#C4A052]/15 text-[#C4A052] border border-[#C4A052]/30"
                                )}>
                                  {wg.source}
                                </span>
                                {wpEmailList.length > 0 && (
                                  <span className={cn("text-xs truncate max-w-[250px]",
                                    wpPaused ? "text-red-200/80" : wpListed ? "text-blue-200/80" : "text-zinc-500"
                                  )}>
                                    {wpEmailList.join(', ')}
                                  </span>
                                )}
                                <div className="flex items-center gap-2 ml-auto">
                                  <PriceInput listedKey={wpListedKey} />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleToggleDiffuse(wg.tickets || [])}
                                    className={cn(
                                      "h-7 px-2 text-xs font-bold rounded-full transition-all flex items-center gap-1.5 min-w-[110px] justify-center",
                                      (wg.tickets || []).length > 0 && (wg.tickets || []).every(t => isTicketDiffused(t))
                                        ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-md ring-1 ring-emerald-300"
                                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-600"
                                    )}
                                    data-testid={`btn-diffuse-${wpListedKey}`}
                                    title="Diffuser sur la page publique"
                                  >
                                    <span className={cn(
                                      "inline-block h-3 w-3 rounded-full",
                                      (wg.tickets || []).length > 0 && (wg.tickets || []).every(t => isTicketDiffused(t))
                                        ? "bg-white"
                                        : "bg-zinc-500"
                                    )} />
                                    <Radio className="h-3 w-3" />
                                    {(wg.tickets || []).length > 0 && (wg.tickets || []).every(t => isTicketDiffused(t)) ? 'DIFFUSÉ · ON' : 'DIFFUSER · OFF'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleListed(wpListedKey, wg.tickets || [])}
                                    className={cn(
                                      "h-7 px-3 text-xs font-bold rounded transition-all",
                                      wpListed
                                        ? "bg-blue-500 text-white hover:bg-blue-600 shadow-md ring-1 ring-blue-300"
                                        : wpPaused
                                        ? "bg-red-500 text-white hover:bg-red-600 shadow-md ring-1 ring-red-300"
                                        : "bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700 border border-zinc-600"
                                    )}
                                    data-testid={`btn-listed-${wpListedKey}`}
                                    title={wpPaused ? "Clic : retour à À LISTER" : wpListed ? "Clic : passer en PAUSE manuelle" : "Clic : marquer DÉJÀ LISTÉ"}
                                  >
                                    {wpPaused ? '⏸ EN PAUSE' : wpListed ? '✓ DÉJÀ LISTÉ' : 'LISTER'}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AideListingView;
